import * as React from 'react'
import { t } from 'ttag'
import { DivisionDto, DivisionId, GroupView } from '../../services/api.types'
import YumButton from '../YumButton/YumButton'
import SwipeableDrawer from '@material-ui/core/SwipeableDrawer'
import { ReactComponent as FilterIcon } from '../../assets/v2/icons/icon-filter.svg'
import { ReactComponent as FilterIconNew } from '../../assets/v3/icons/ranking/filter.svg'
import { ReactComponent as FilterActiveIcon } from '../../assets/v2/icons/icon-filter-active.svg'
import { ReactComponent as CountriesSeparatedIcon } from '../../assets/v2/icons/icon-countries-separated.svg'
import { ReactComponent as CountriesMergedIcon } from '../../assets/v2/icons/icon-countries-merged.svg'
import { ReactComponent as OnlyMyCountryIcon } from '../../assets/v2/icons/icon-only-my-country.svg'
import { ReactComponent as OnlyMyGroupIcon } from '../../assets/v2/icons/icon-only-my-group.svg'
import { ReactComponent as CollapseClosedIcon } from '../../assets/v2/icons/icon-collapse-closed.svg'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import Chip from '@material-ui/core/Chip'
import { countryWithFlag, localizeCountries, LocalizedCountry } from '../YumCountrySelect/YumCountrySelect'
import { ActivityView } from '../../views/Activity/store/ActivityReducer'
import { List, ListItem, ListItemIcon } from '@material-ui/core'
import { ReactComponent as CheckedCircleIcon } from '../../assets/v2/icons/checked-circle-blue.svg'
import { ReactComponent as EmptyCircleIcon } from '../../assets/v2/icons/empty-circle-blue.svg'
import ListItemText from '@material-ui/core/ListItemText'
import Collapse from '@material-ui/core/Collapse'
import { Capacitor } from '@capacitor/core'
import { isRtl } from '../../i18nInit'

export interface IYumActivityFilterProps {
  default: {
    countries: string[]
    divisionIds: DivisionId[]
  }
  current: {
    countries: string[]
    divisionIds: DivisionId[]
  }
  countries: string[]
  divisions: DivisionDto[]
  activityView: ActivityView
  groupView: GroupView
  isAdmin: boolean
  country: string
  divisionId: DivisionId
  setActivityFilter(activityFilter: any): any
  setGroupView(groupView: GroupView): any
  size?: 'small' | 'medium' | 'large'
}

export interface IYumActivityFilterState {
  isDrawerOpen: boolean
  filterActive: boolean
  groupFilterActive: boolean
  activityFilter: {
    countries: string[]
    divisionIds: DivisionId[]
  }
  groupView: GroupView
  groupCollapsed: boolean
  countryCollapsed: boolean
}

export class YumActivityFilter extends React.PureComponent<IYumActivityFilterProps, IYumActivityFilterState> {
  state = {
    isDrawerOpen: false,
    filterActive: this.props.current && JSON.stringify(this.props.default) !== JSON.stringify(this.props.current),
    groupFilterActive: this.props.groupView && this.props.groupView !== GroupView.COUNTRIES_MERGED,
    activityFilter: this.props.current || this.props.default,
    groupView: this.props.groupView,
    groupCollapsed: false,
    countryCollapsed: !!(this.props.divisions && this.props.divisions.length),
  }

  reset = () => {
    if (this.props.activityView === ActivityView.INDIVIDUAL) {
      this.props.setActivityFilter(this.props.default)
      this.setState({ isDrawerOpen: false, filterActive: false, activityFilter: this.props.default })
    } else {
      this.props.setGroupView(GroupView.COUNTRIES_MERGED)
      this.setState({ isDrawerOpen: false, groupFilterActive: false, groupView: GroupView.COUNTRIES_MERGED })
    }
  }

  filter = () => {
    if (this.props.activityView === ActivityView.INDIVIDUAL) {
      this.props.setActivityFilter(this.state.activityFilter)
      const filterActive = JSON.stringify(this.props.default) !== JSON.stringify(this.state.activityFilter)
      this.setState({ filterActive })
    } else {
      this.props.setGroupView(this.state.groupView)
      const groupFilterActive = this.props.groupView && GroupView.COUNTRIES_MERGED !== this.state.groupView
      this.setState({ groupFilterActive })
    }
    this.setState({ isDrawerOpen: false })
  }

  toggleDivision = (divisionId: DivisionId) => () => {
    if (!this.state.activityFilter.divisionIds.includes(divisionId)) {
      this.setState({
        activityFilter: {
          ...this.state.activityFilter,
          divisionIds: [...this.state.activityFilter.divisionIds, divisionId],
        },
      })
    } else {
      this.setState({
        activityFilter: {
          ...this.state.activityFilter,
          divisionIds: this.state.activityFilter.divisionIds.filter(function (d) {
            return d !== divisionId
          }),
        },
      })
    }
  }

  toggleCountry = (country: string) => () => {
    if (!this.state.activityFilter.countries.includes(country)) {
      this.setState({
        activityFilter: {
          ...this.state.activityFilter,
          countries: [...this.state.activityFilter.countries, country],
        },
      })
    } else {
      this.setState({
        activityFilter: {
          ...this.state.activityFilter,
          countries: this.state.activityFilter.countries.filter(function (c) {
            return c !== country
          }),
        },
      })
    }
  }

  handleGroupViewClick = (groupView: GroupView) => () => {
    this.setState({ groupView })
  }

  toggleDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (
      event &&
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' || (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return
    }

    this.setState({ isDrawerOpen: open })
  }

  toggleGroupCollapse = () => {
    this.setState({ groupCollapsed: !this.state.groupCollapsed })
  }

  toggleCountryCollapse = () => {
    this.setState({ countryCollapsed: !this.state.countryCollapsed })
  }

  render() {
    const iOS = Capacitor.getPlatform() === 'ios'
    const divisions = this.props.divisions || []
    const countries = this.props.countries || []

    const groupViewOptions = [
      {
        key: GroupView.COUNTRIES_MERGED,
        label: t`Countries merged`,
      },
      {
        key: GroupView.COUNTRIES_SEPARATED,
        label: t`Countries separated`,
      },
    ]

    if (!this.props.isAdmin) {
      if (this.props.country) {
        groupViewOptions.push({
          key: GroupView.ONLY_MY_COUNTRY,
          label: t`Only my country (by groups)`,
        })
      }
      if (this.props.divisionId) {
        groupViewOptions.push({
          key: GroupView.ONLY_MY_GROUP,
          label: t`Only my group (by countries)`,
        })
      }
    }

    return (countries && countries.length) || (divisions && divisions.length) ? (
      <>
        <YumButton
          variant={this.props.size === 'medium' ? 'text' : 'contained'}
          size={this.props.size || 'small'}
          style={isRtl ? { marginRight: '10px' } : { marginLeft: '10px' }}
          onClick={this.toggleDrawer(true)}
        >
          {this.props.size === 'medium' ? <FilterIconNew /> : <FilterIcon style={{ marginTop: -2 }} />}
          <Box
            display={{ xs: 'none', md: 'inline' }}
            style={isRtl ? { marginRight: 4 } : { marginLeft: 4 }}
          >{t`Filter`}</Box>
          {(
            this.props.activityView === ActivityView.INDIVIDUAL ? this.state.filterActive : this.state.groupFilterActive
          ) ? (
            <FilterActiveIcon
              style={{
                position: 'absolute',
                top: -2,
                right: -2,
              }}
            />
          ) : (
            ''
          )}
        </YumButton>
        <SwipeableDrawer
          anchor="bottom"
          open={this.state.isDrawerOpen}
          onClose={this.toggleDrawer(false)}
          onOpen={this.toggleDrawer(true)}
          disableBackdropTransition={!iOS}
          disableDiscovery={iOS}
        >
          <div style={{ padding: '8px 10px 18px 10px' }}>
            {this.props.activityView === ActivityView.INDIVIDUAL ? (
              <>
                {divisions && divisions.length ? (
                  <div style={{ marginBottom: 20 }}>
                    <Collapse
                      in={!this.state.groupCollapsed}
                      collapsedSize={50}
                      style={{
                        boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.15)',
                        borderRadius: 20,
                      }}
                    >
                      <div onClick={this.toggleGroupCollapse} style={{ height: 50, padding: 15, cursor: 'pointer' }}>
                        <CollapseClosedIcon
                          style={{
                            float: isRtl ? 'left' : 'right',
                            transition: '.4s all',
                            transform: this.state.groupCollapsed ? '' : 'rotate(-180deg)',
                          }}
                        />
                        <Typography variant="h2" style={{ lineHeight: '20px' }}>{t`Team`}</Typography>
                      </div>
                      <div style={{ padding: '5px 15px 10px 15px' }}>
                        {divisions.map(({ id, name }, index) => {
                          return (
                            <Chip
                              key={index}
                              label={name}
                              onClick={this.toggleDivision(id)}
                              variant={
                                this.state.activityFilter.divisionIds &&
                                this.state.activityFilter.divisionIds.includes(id)
                                  ? 'outlined'
                                  : 'default'
                              }
                            />
                          )
                        })}
                      </div>
                    </Collapse>
                  </div>
                ) : (
                  ''
                )}

                {countries && countries.length ? (
                  <div style={{ marginBottom: 20 }}>
                    <Collapse
                      in={!this.state.countryCollapsed}
                      collapsedSize={50}
                      style={{
                        boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.15)',
                        borderRadius: 20,
                      }}
                    >
                      <div onClick={this.toggleCountryCollapse} style={{ height: 50, padding: 15, cursor: 'pointer' }}>
                        <CollapseClosedIcon
                          style={{
                            float: isRtl ? 'left' : 'right',
                            transition: '.4s all',
                            transform: this.state.countryCollapsed ? '' : 'rotate(-180deg)',
                          }}
                        />
                        <Typography variant="h2" style={{ lineHeight: '20px' }}>{t`Country`}</Typography>
                      </div>
                      <div style={{ padding: '5px 15px 10px 15px' }}>
                        {localizeCountries(countries).map(
                          ({ code, countryWithFlag }: LocalizedCountry, index: number) => {
                            return (
                              <Chip
                                key={index}
                                label={countryWithFlag}
                                onClick={this.toggleCountry(code)}
                                variant={
                                  this.state.activityFilter.countries &&
                                  this.state.activityFilter.countries.includes(code)
                                    ? 'outlined'
                                    : 'default'
                                }
                              />
                            )
                          },
                        )}
                      </div>
                    </Collapse>
                  </div>
                ) : (
                  ''
                )}
              </>
            ) : (
              <>
                <Typography variant="h2">{t`View options`}</Typography>
                <List>
                  {groupViewOptions.map(({ key, label }, index) => {
                    return (
                      <ListItem
                        key={key}
                        role={undefined}
                        button
                        disableRipple
                        onClick={this.handleGroupViewClick(key)}
                        selected={this.state.groupView === key}
                      >
                        <ListItemIcon>
                          {this.state.groupView === key ? <CheckedCircleIcon /> : <EmptyCircleIcon />}
                        </ListItemIcon>
                        <ListItemText id={key} primary={label} />
                        <ListItemIcon style={{ minWidth: 'auto' }}>
                          <>
                            {key === GroupView.COUNTRIES_SEPARATED ? <CountriesSeparatedIcon /> : ''}
                            {key === GroupView.COUNTRIES_MERGED ? <CountriesMergedIcon /> : ''}
                            {key === GroupView.ONLY_MY_COUNTRY ? <OnlyMyCountryIcon /> : ''}
                            {key === GroupView.ONLY_MY_GROUP ? <OnlyMyGroupIcon /> : ''}
                          </>
                        </ListItemIcon>
                      </ListItem>
                    )
                  })}
                </List>
              </>
            )}
          </div>

          <Box style={{ margin: '12px auto 0' }}>
            <YumButton
              variant="outlined"
              onClick={this.reset}
              style={{
                display: 'inline-block',
                margin: isRtl ? '0 0 0 10px' : '0 10px 0 0',
                width: 'auto',
                padding: '5px 20px',
              }}
            >{t`Reset`}</YumButton>
            <YumButton
              onClick={this.filter}
              style={{ display: 'inline-block', margin: isRtl ? '0 10px 0 0' : '0 0 0 10px', width: 155 }}
            >{t`Filter`}</YumButton>
          </Box>
        </SwipeableDrawer>
      </>
    ) : (
      ''
    )
  }
}
